import React from 'react';
import { X, Search } from 'lucide-react';

const PaymentDetailsModal = ({
  selectedPayment,
  paymentDetails,
  paymentByIdLoading,
  paymentDetailsLoading,
  paymentByIdError,
  paymentDetailsError,
  handleCloseDetails,
  getPaymentPlanBadgeColor,
  getStatusBadgeColor
}) => {
  if (!selectedPayment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Payment Details</h2>
          <button 
            onClick={handleCloseDetails}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6">
          {(paymentByIdLoading || paymentDetailsLoading) && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="mt-2 text-gray-600">Loading payment details...</p>
            </div>
          )}

          {(paymentByIdError || paymentDetailsError) && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <strong>Error:</strong> {paymentByIdError || paymentDetailsError}
            </div>
          )}

          {!paymentByIdLoading && !paymentDetailsLoading && (
            <>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Payment ID</label>
                    <p className="font-semibold text-gray-900">{selectedPayment.payment_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Invoice No</label>
                    <p className="font-semibold text-gray-900">{selectedPayment.invoice_no}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">License ID</label>
                    <p className="font-semibold text-gray-900">{selectedPayment.license_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Payment Plan</label>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentPlanBadgeColor(selectedPayment.payment_plan)}`}>
                      {selectedPayment.payment_plan}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Total Amount</label>
                    <p className="font-semibold text-gray-900">LKR {selectedPayment.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Paid Amount</label>
                    <p className="font-semibold text-gray-900">LKR {selectedPayment.paid_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Balance</label>
                    <p className={`font-semibold ${selectedPayment.balance_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      LKR {selectedPayment.balance_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Status</label>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(selectedPayment.status)}`}>
                      {selectedPayment.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Created Date</label>
                    <p className="font-semibold text-gray-900">{selectedPayment.created_date}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">Payment Transactions</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paymentDetails && Array.isArray(paymentDetails) && paymentDetails.length > 0 ? (
                        paymentDetails.map((detail, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {detail.payment_date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {detail.payment_method}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {detail.reference_no}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                              LKR {detail.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {detail.remarks}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                            No payment transactions found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={handleCloseDetails}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsModal;